import { useEffect, useState } from 'react';
import config from '../config';

const SHARE_HOST = config.SITE_MAP_SHARE_HOST;
const API_BASE   = config.SITE_MAP_API_BASE;

// Strict RFC-4122-shaped UUID match. Tighter than `[0-9a-f-]{36}` so a paste
// of 36 hyphens or other malformed garbage doesn't sail through and trigger a
// 404 fetch.
const SHARE_RE = /^https?:\/\/[^/]+\/share\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;

// Validates the producer-side bounds shape: `{sw: [lng,lat], ne: [lng,lat]}`.
// A malformed payload (`{}`, `sw: null`, `sw: [lng]`) would otherwise crash
// FitBounds at call sites that index `bounds.sw[1]`.
function validBounds(b: any): boolean {
  return !!b
    && Array.isArray(b.sw) && b.sw.length >= 2
    && Array.isArray(b.ne) && b.ne.length >= 2
    && Number.isFinite(b.sw[0]) && Number.isFinite(b.sw[1])
    && Number.isFinite(b.ne[0]) && Number.isFinite(b.ne[1]);
}

export type SiteMapState =
  | 'idle'      // no URL configured on the event
  | 'invalid'   // URL set but not a producer share link
  | 'loading'   // URL parsed, fetch in flight
  | 'broken'    // fetch errored (most often 404 = producer event deleted)
  | 'ready'     // fetched and at least one site map exists
  | 'empty';    // fetched but producer event has no site maps yet

export interface SiteMapShare {
  state: SiteMapState;
  shareUrl: string | null;
  viewUrl: string | null;
  editUrl: string | null;
  geojson: any;
  textLabels: any[];
  placedImages: any[];
  mapSettings: any;
  bounds: { sw: [number, number]; ne: [number, number] } | null;
  siteMap: any;
  loading: boolean;
  error: string | null;
}

export function parseShareUrl(url?: string | null): string | null {
  if (!url) return null;
  const m = url.match(SHARE_RE);
  return m ? m[1] : null;
}

/**
 * Fetches and exposes a producer share payload.
 *
 * @param siteMapUrl Raw URL stored on the Firefleet event.
 * @param refreshKey Bump to force a refetch (used after iframe edits).
 */
export function useSiteMapShare(
  siteMapUrl?: string | null,
  refreshKey: number = 0
): SiteMapShare {
  const eventUuid = parseShareUrl(siteMapUrl);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventUuid) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Cross-origin to studio. `same-origin` is the browser default for bare
    // fetch, but spelling `omit` here is hardening against a future global
    // fetch wrapper that flips the default.
    fetch(`${API_BASE}/share/${eventUuid}/`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch((e) => {
        if (!cancelled) {
          setData(null);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventUuid, refreshKey]);

  // Only the first site_map per producer event is surfaced; producer's data
  // model allows more, but in practice events have one. Add multi-map UI when
  // a real use case appears.
  const siteMap = data?.site_maps?.[0] ?? null;
  const rawBounds = siteMap?.show_coordinates;
  const bounds = validBounds(rawBounds) ? rawBounds : null;

  let state: SiteMapState = 'idle';
  if (!siteMapUrl) state = 'idle';
  else if (!eventUuid) state = 'invalid';
  else if (loading) state = 'loading';
  else if (error) state = 'broken';
  else if (!siteMap) state = 'empty';
  else state = 'ready';

  return {
    state,
    shareUrl: siteMapUrl ?? null,
    viewUrl: eventUuid && state === 'ready' ? `https://${SHARE_HOST}/share/${eventUuid}` : null,
    editUrl: eventUuid && siteMap ? `https://${SHARE_HOST}/event/${eventUuid}/sitemap/${siteMap.id}` : null,
    geojson: siteMap?.geojson ?? null,
    textLabels: siteMap?.text_labels ?? [],
    placedImages: siteMap?.placed_images ?? [],
    mapSettings: siteMap?.map_settings ?? null,
    bounds,
    siteMap,
    loading,
    error,
  };
}
